import React, { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import imagenHomeDark from "../../assets/images/imagenhome.png";
import imagenHomeLight from "../../assets/images/imagen1.png";
import fondoInicio from "../../assets/images/site/fondo-inicio.webp";
import { useTheme } from "../../context/ThemeContext";
import { fadeInUp, scaleIn } from "../../utils/animations";
import "./home.css";

function HeroSection() {
    const { theme } = useTheme();
    const { t } = useTranslation();
    const heroImage = theme === "dark" ? imagenHomeDark : imagenHomeLight;

    const typewriterPhrases = useMemo(
        () => t("hero.phrases", { returnObjects: true }),
        [t],
    );
    const longestTypewriterLine = useMemo(
        () =>
            typewriterPhrases.reduce(
                (longest, phrase) => (phrase.length > longest.length ? phrase : longest),
                "",
            ),
        [typewriterPhrases],
    );

    const [phraseIndex, setPhraseIndex] = useState(0);
    const [displayText, setDisplayText] = useState("");
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        setPhraseIndex(0);
        setDisplayText("");
        setIsDeleting(false);
    }, [typewriterPhrases]);

    useEffect(() => {
        const current = typewriterPhrases[phraseIndex] || "";
        const timeout = setTimeout(() => {
            if (!isDeleting) {
                if (displayText.length < current.length) {
                    setDisplayText(current.slice(0, displayText.length + 1));
                } else {
                    setTimeout(() => setIsDeleting(true), 1800);
                }
            } else if (displayText.length > 0) {
                setDisplayText(current.slice(0, displayText.length - 1));
            } else {
                setIsDeleting(false);
                setPhraseIndex((prev) => (prev + 1) % typewriterPhrases.length);
            }
        }, isDeleting ? 40 : 70);

        return () => clearTimeout(timeout);
    }, [displayText, isDeleting, phraseIndex, typewriterPhrases]);

    return (
        <section className="hero-section">
            <div className="section-bg" aria-hidden="true">
                <img src={fondoInicio} alt="" className="section-bg-img section-bg-img--inicio" decoding="async" />
            </div>
            <div className="hero-bg-gradient" aria-hidden="true">
                <motion.div
                    className="hero-bg-orb hero-bg-orb-1"
                    animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
                    transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
                />
                <motion.div
                    className="hero-bg-orb hero-bg-orb-2"
                    animate={{ x: [0, -25, 0], y: [0, 25, 0] }}
                    transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
                />
                <motion.div
                    className="hero-bg-orb hero-bg-orb-3"
                    animate={{ scale: [1, 1.08, 1], opacity: [0.35, 0.5, 0.35] }}
                    transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                />
            </div>

            <div className="hero-container mt-14">
                <motion.div
                    className="hero-text-content"
                    initial="hidden"
                    animate="visible"
                    variants={{
                        hidden: {},
                        visible: { transition: { staggerChildren: 0.15 } },
                    }}
                >
                    <motion.div className="hero-badge" variants={fadeInUp}>
                        <span className="hero-badge-dot" />
                        <span>{t("hero.badge")}</span>
                    </motion.div>

                    <motion.h1 className="hero-title" variants={fadeInUp}>
                        {t("hero.titleBefore")}{" "}
                        <span className="hero-title-gradient">{t("hero.titleGradient")}</span>
                    </motion.h1>

                    <motion.p className="hero-subtitle" variants={fadeInUp}>
                        {t("hero.subtitle")}
                    </motion.p>

                    <motion.p className="hero-specialty" variants={fadeInUp}>
                        <span className="hero-typewriter-wrap">
                            <span className="hero-typewriter-text">
                                {t("hero.specialtyPrefix")} {displayText}
                                <span className="hero-typewriter-cursor" aria-hidden>|</span>
                            </span>
                            <span className="hero-typewriter-measure" aria-hidden="true">
                                {t("hero.specialtyPrefix")} {longestTypewriterLine}
                            </span>
                        </span>
                    </motion.p>

                    <motion.div className="hero-actions" variants={fadeInUp}>
                        <motion.a
                            href="#contacto"
                            className="btn-primary"
                            whileHover={{ scale: 1.04, y: -2 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            {t("hero.ctaPrimary")}
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" className="btn-icon">
                                <path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clipRule="evenodd" />
                            </svg>
                        </motion.a>
                        <motion.a
                            href="#equipo"
                            className="btn-secondary"
                            whileHover={{ scale: 1.03, y: -2 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            {t("hero.ctaSecondary")}
                        </motion.a>
                    </motion.div>
                </motion.div>

                <motion.div
                    className="hero-img-wrapper"
                    initial="hidden"
                    animate="visible"
                    variants={scaleIn}
                    transition={{ delay: 0.3 }}
                >
                    <motion.img
                        src={heroImage}
                        alt={t("hero.imgAlt")}
                        className={`hero-img shadow-2xl relative z-10 ${theme === "light" ? "hero-img--light" : ""}`}
                        animate={{ y: [0, -8, 0] }}
                        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                    />
                    <div className="hero-img-glow" aria-hidden="true" />
                </motion.div>
            </div>
        </section>
    );
}

export default HeroSection;
